import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import FieldError from '../../../../../../components/helpers/FieldError';

import style from './style.module.css';

const valueType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);
const optionsType = PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: valueType }));

/**
 * Native select of the target signer fields.
 *
 * @param {object} props Component props.
 * @param {string} props.name Select name.
 * @param {Array} props.options Options list with #name and #value fields.
 * @param {string|number} props.value Selected value.
 * @param {Function} props.onChange Change handler.
 * @param {string} [props.error] Validation error message.
 */
const SignerSelect = memo(({ name, options, value, onChange, error }) => (
  <div className={classNames({ 'has-error': error })}>
    <select className="form-control" disabled={!options.length} name={name} onChange={onChange} value={value ?? ''}>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.name}
        </option>
      ))}
    </select>
    <FieldError message={error} />
  </div>
));

SignerSelect.propTypes = {
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: optionsType.isRequired,
  value: valueType,
};

const signerShape = PropTypes.shape({
  signingOrder: PropTypes.number.isRequired,
  signsFor: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  email: PropTypes.string,
});

/**
 * NDA DocuSign signers table.
 */
const NdaSigners = ({
  targetSigner,
  harveySigner,
  contactOptions,
  emailOptions,
  contactId,
  contactEmailId,
  errors,
  onContactChange,
  onEmailChange,
}) => (
  <div className={style.host}>
    <h3 className={style.title}>Signers</h3>
    <table className={style.table}>
      <thead>
        <tr>
          <th>Signs for</th>
          <th className={style.order}>Signing Order</th>
          <th>Name</th>
          <th>Title</th>
          <th>Email</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={style.signsFor}>{targetSigner.signsFor}</td>
          <td className={style.order}>{targetSigner.signingOrder}</td>
          <td>
            <SignerSelect
              error={errors.contactId}
              name="signerContact"
              onChange={onContactChange}
              options={contactOptions}
              value={contactId}
            />
          </td>
          <td>{targetSigner.title}</td>
          <td>
            <SignerSelect
              error={errors.contactEmailId}
              name="signerEmail"
              onChange={onEmailChange}
              options={emailOptions}
              value={contactEmailId}
            />
          </td>
        </tr>
        <tr>
          <td className={style.signsFor}>{harveySigner.signsFor}</td>
          <td className={style.order}>{harveySigner.signingOrder}</td>
          <td>{harveySigner.name}</td>
          <td>{harveySigner.title}</td>
          <td>{harveySigner.email}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

NdaSigners.propTypes = {
  contactEmailId: valueType,
  contactId: valueType,
  contactOptions: optionsType.isRequired,
  emailOptions: optionsType.isRequired,
  errors: PropTypes.shape({ contactId: PropTypes.string, contactEmailId: PropTypes.string }).isRequired,
  harveySigner: signerShape.isRequired,
  onContactChange: PropTypes.func.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  targetSigner: signerShape.isRequired,
};

export default memo(NdaSigners);
